// frontend/src/lib/useFocusTrap.ts
//
// t2 #5: the audio-setup overlay and the begin/resume gate were plain fixed
// divs. `document.querySelector("[role=dialog]")` returned nothing, two Tabs
// from PLAY TEST put focus on the page behind the overlay, and a screen reader
// was never told a dialog had opened. This is the missing half: the role and
// the label go on the element, and this hook keeps focus inside it and gives
// it back where it came from on close.
//
// Escape is deliberately NOT handled here. Some of these dialogs can be
// dismissed and some are gates with no dismissal (setup, the resume gate), so
// each caller decides, and the ones that dismiss already own that key.
import { useEffect, useRef } from 'react';
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
/**
 * Trap focus inside the returned element while ``active``.
 *
 * Moves focus in on mount (respecting an autoFocus the dialog set itself),
 * cycles Tab and Shift+Tab at the edges, pulls focus back if anything outside
 * takes it, and restores the previously focused element on unmount.
 */
export function useFocusTrap<T extends HTMLElement>(active = true) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    const restoreTo = document.activeElement as HTMLElement | null;
    // No visibility filtering: jsdom reports every element as unrendered, and
    // a dialog does not render hidden controls anyway.
    const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!node.contains(document.activeElement)) {
      const first = focusables()[0];
      if (first) first.focus();
      else { node.tabIndex = -1; node.focus(); }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const here = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (here === first || !node.contains(here))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (here === last || !node.contains(here))) {
        e.preventDefault();
        first.focus();
      }
    };
    // Clicks and programmatic focus can land outside without a Tab; pull back.
    const onFocusIn = (e: FocusEvent) => {
      if (node.contains(e.target as Node)) return;
      const items = focusables();
      (items[0] ?? node).focus();
    };
    node.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      node.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      if (restoreTo && document.contains(restoreTo)) restoreTo.focus();
    };
  }, [active]);
  return ref;
}
